import { Check, X } from 'lucide-react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import type { Media, MediaKind } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaThumb, MediaUploadButton, useMediaList, useMediaUploader } from '@/features/media';

import { isReviewMediaType, isTooLarge, REVIEW_MEDIA_TYPES } from '../schemas/file-limits';

const ALL = '__all__';
const PAGE_SIZE = 12;

export interface MediaMultiPickerProps {
  value: string[];
  onChange: (value: string[]) => void;
  /** Review media: images and videos only, 20 MB each, checked before anything is sent. */
  reviewMedia?: boolean;
  max?: number;
  label: string;
  invalid?: boolean;
}

/**
 * Several files from the organisation's media library, picked in place inside a dialog, plus an
 * upload that lands in the library and is selected at once. Sizes are checked here so a 21 MB
 * video is refused before the request instead of after it.
 */
export function MediaMultiPicker({
  value,
  onChange,
  reviewMedia = false,
  max = 10,
  label,
  invalid = false,
}: MediaMultiPickerProps) {
  const { t } = useTranslation('production');
  const [kind, setKind] = useState<MediaKind | undefined>(reviewMedia ? undefined : 'document');
  const [problem, setProblem] = useState<string | null>(null);
  const [picked, setPicked] = useState<Record<string, Media>>({});
  const query = useMediaList({ limit: PAGE_SIZE, ...(kind ? { kind } : {}) });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const add = useCallback(
    (media: Media) => {
      setPicked((current) => ({ ...current, [media.id]: media }));
      onChange(value.includes(media.id) || value.length >= max ? value : [...value, media.id]);
    },
    [max, onChange, value],
  );
  // The upload queue is rebuilt whenever its callback changes, which would drop an upload in
  // flight; the callback stays stable and reads the latest selection through a ref.
  const latestAdd = useRef(add);
  useLayoutEffect(() => {
    latestAdd.current = add;
  });
  const onUploaded = useCallback((media: Media) => latestAdd.current(media), []);
  const uploader = useMediaUploader({ onUploaded });
  const uploading = uploader.items.filter(
    (item) => item.status === 'uploading' || item.status === 'queued',
  );
  const failed = uploader.items.filter((item) => item.status === 'error');

  const refusal = (media: Pick<Media, 'mimeType' | 'bytes' | 'alt'>, name: string) => {
    if (!reviewMedia) return null;
    if (!isReviewMediaType(media.mimeType)) return t('media.wrongType', { name });
    if (isTooLarge(media.bytes)) return t('media.tooLarge', { name });
    return null;
  };

  const toggle = (media: Media) => {
    const name = media.alt ?? t('media.untitled');
    if (value.includes(media.id)) {
      onChange(value.filter((id) => id !== media.id));
      return;
    }
    const refused = refusal(media, name);
    setProblem(refused);
    if (!refused) add(media);
  };

  const onFiles = (files: File[]) => {
    const accepted: File[] = [];
    for (const file of files) {
      const refused = refusal({ mimeType: file.type, bytes: file.size, alt: file.name }, file.name);
      if (refused) setProblem(refused);
      else accepted.push(file);
    }
    if (accepted.length > 0) {
      setProblem(null);
      uploader.add(accepted);
    }
  };

  return (
    <div
      className={cn('flex flex-col gap-3', invalid && 'border-destructive rounded-md border p-2')}
      role="group"
      aria-label={label}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={kind ?? ALL}
          onValueChange={(next) => setKind(next === ALL ? undefined : (next as MediaKind))}
        >
          <SelectTrigger className="w-[160px]" aria-label={t('media.kind')}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('media.allKinds')}</SelectItem>
            {(reviewMedia
              ? (['image', 'video'] as const)
              : (['document', 'image', 'video'] as const)
            ).map((option) => (
              <SelectItem key={option} value={option}>
                {t(`media.kinds.${option}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <MediaUploadButton
          variant="outline"
          size="sm"
          onFiles={onFiles}
          aria-label={t('media.uploadLabel', { name: label })}
          {...(reviewMedia ? { accept: REVIEW_MEDIA_TYPES } : {})}
        >
          {t('media.upload')}
        </MediaUploadButton>
      </div>
      {problem ? (
        <p role="alert" className="text-destructive text-sm">
          {problem}
        </p>
      ) : null}
      {uploading.map((item) => (
        <p key={item.id} className="text-muted-foreground text-xs">
          {t('media.uploading', { name: item.file.name })}
        </p>
      ))}
      {failed.map((item) => (
        <p key={item.id} className="text-destructive text-xs">
          {t('media.uploadFailed', { name: item.file.name })}
        </p>
      ))}
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="aspect-square w-full rounded-md" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-4 text-sm">
          {t('media.empty')}
        </p>
      ) : (
        <ul
          aria-label={t('media.library')}
          className="grid max-h-64 grid-cols-3 gap-2 overflow-y-auto p-0.5 sm:grid-cols-4"
        >
          {items.map((media) => {
            const selected = value.includes(media.id);
            const name = media.alt ?? t('media.untitled');
            const tooLarge = reviewMedia && isTooLarge(media.bytes);
            return (
              <li key={media.id}>
                <button
                  type="button"
                  aria-pressed={selected}
                  aria-label={name}
                  onClick={() => toggle(media)}
                  className="group focus-visible:ring-ring/50 relative block w-full rounded-md text-left outline-none focus-visible:ring-[3px]"
                >
                  <MediaThumb
                    media={media}
                    className={cn(
                      'w-full border transition-colors',
                      selected && 'ring-primary ring-2',
                    )}
                  />
                  {selected ? (
                    <span className="bg-primary text-primary-foreground absolute top-1.5 right-1.5 flex size-5 items-center justify-center rounded-full">
                      <Check className="size-3" aria-hidden />
                    </span>
                  ) : null}
                  {tooLarge ? (
                    <Badge variant="destructive" className="absolute bottom-6 left-1">
                      {t('media.tooLargeBadge')}
                    </Badge>
                  ) : null}
                  <span className="mt-1 block truncate text-[11px]" aria-hidden>
                    {name}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {query.hasNextPage ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="self-center"
          disabled={query.isFetchingNextPage}
          onClick={() => void query.fetchNextPage()}
        >
          {query.isFetchingNextPage ? t('media.loadingMore') : t('media.loadMore')}
        </Button>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <p className="text-sm font-medium">{t('send.picked')}</p>
        {value.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('send.noneSelected')}</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5" aria-label={t('send.picked')}>
            {value.map((id) => {
              const media = picked[id] ?? items.find((m) => m.id === id);
              const name = media?.alt ?? t('media.untitled');
              return (
                <li key={id}>
                  <Badge variant="secondary" className="gap-1 pr-1">
                    {name}
                    <button
                      type="button"
                      className="hover:text-foreground rounded-full"
                      aria-label={t('send.remove', { name })}
                      onClick={() => onChange(value.filter((v) => v !== id))}
                    >
                      <X className="size-3" aria-hidden />
                    </button>
                  </Badge>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
